import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Typography } from "@mui/material";
import { ResourceExplorerTableAccountCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/columns-renderers/ResourceExplorerTableAccountCell";
import {
  ForecastOption,
  ResourceExplorerGroupingNullable,
} from "@/services/cloudchipr.api";
import { TypographyWithCopy } from "@/components/common/TypographyWithCopy";
import { ResourceExplorerTotalCostColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/ResourceExplorerTotalCostColumnHeaderCell";
import { money } from "@/utils/numeral/money";
import { ResourceExplorerPrevPeriodHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/ResourceExplorerPrevPeriodHeaderCell";
import { PreviousPeriodCostCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/columns-renderers/PreviousPeriodCostCell";
import { ResourceExplorerTrendColumnHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/ResourceExplorerTrendColumnHeaderCell";
import { TrendCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/columns-renderers/TrendCell";
import { groupingsExcludingForecastedCostsColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/utils/constants/groupingsExcludingForecastedCostsColumns";
import { ResourceExplorerForecastedCostHeaderCell } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/header-cell-components/ResourceExplorerForecastedCostHeaderCell";
import { getBillingExplorerLabelColDef } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/column-defs/getBillingExplorerLabelColDef";

export const billingExplorerResourceGroupingColumnDefs: ColDef[] = [
  {
    field: "metadata.provider_account_name",
    headerName: "Account",
    valueGetter: ({ data }) =>
      `${data.metadata?.provider_account_name || data.metadata?.provider_account_id}`.toLowerCase(),
    cellRenderer: ({ data }: ICellRendererParams) => {
      return <ResourceExplorerTableAccountCell data={data} />;
    },
  },
  {
    field: "metadata.region",
    headerName: "Region",
    valueGetter: ({ data }) => `${data.metadata?.region}`.toLowerCase(),
    cellRenderer: ({ data }: ICellRendererParams) => {
      return (
        <TypographyWithCopy
          variant="body2"
          value={data.metadata?.region ?? "-"}
          maxWidth={140}
        />
      );
    },
  },
  {
    field: "metadata.service",
    headerName: "Service Name",
    valueGetter: ({ data }) => `${data.metadata?.service}`.toLowerCase(),
    cellRenderer: ({ data }: ICellRendererParams) => {
      return (
        <TypographyWithCopy
          variant="body2"
          value={data.metadata?.service ?? "-"}
          maxWidth={140}
        />
      );
    },
  },
];

export const billingExplorerTotalCostColumn: ColDef = {
  minWidth: 160,
  field: "total_cost",
  headerName: "Total Cost",
  headerComponentParams: {
    innerHeaderComponent: ResourceExplorerTotalCostColumnHeaderCell,
  },
  cellRenderer: ({ value }: ICellRendererParams) => {
    return (
      <Typography variant="body2" align="right" pr={8}>
        {isNaN(value) ? "N/A" : money(value)}
      </Typography>
    );
  },
};

export const billingExplorerPreviousPeriodCostColumn: ColDef = {
  minWidth: 230,
  field: "previous_period_total_cost",
  headerName: "Previous Period Cost",
  headerComponentParams: {
    innerHeaderComponent: ResourceExplorerPrevPeriodHeaderCell,
  },
  cellRenderer: ({ value }: ICellRendererParams) => {
    return <PreviousPeriodCostCell value={value} />;
  },
};

export const billingExplorerTrendColumn: ColDef = {
  field: "trend",
  headerComponentParams: {
    innerHeaderComponent: ResourceExplorerTrendColumnHeaderCell,
  },
  cellRenderer: ({ value }: ICellRendererParams) => {
    return <TrendCell value={value} />;
  },
};

export const getBillingExplorerSingleForecastedCostColumnDefs = (
  forecastOption: ForecastOption,
): ColDef => {
  const headerName = forecastOption === "month" ? "Month-end" : "Quarter-end";

  return {
    minWidth: forecastOption === "month" ? 280 : 290,
    hide: forecastOption === "quarter",
    field:
      forecastOption === "month" ? "monthly_forecast" : "quarterly_forecast",
    headerName: `Forecasted ${headerName} Cost`,
    headerComponentParams: {
      innerHeaderComponent: () => (
        <ResourceExplorerForecastedCostHeaderCell
          forecastOption={forecastOption}
        />
      ),
    },
    cellRenderer: ({ value }: ICellRendererParams) => {
      return (
        <Typography variant="body2" align="right" pr={8}>
          {value === null ? "-" : `~${money(value?.cost)}`}
        </Typography>
      );
    },
  };
};

export const getBillingExplorerDataGridColumnDefs = (
  groupBy: ResourceExplorerGroupingNullable,
): ColDef[] => {
  if (groupBy === "resource") {
    return [
      getBillingExplorerLabelColDef({ sortable: true, pinned: false }),
      ...billingExplorerResourceGroupingColumnDefs,
      billingExplorerTotalCostColumn,
      billingExplorerPreviousPeriodCostColumn,
      billingExplorerTrendColumn,
    ];
  }
  const columns: ColDef[] = [
    getBillingExplorerLabelColDef({
      sortable: groupBy !== "none",
      pinned: false,
    }),
    billingExplorerTotalCostColumn,
    billingExplorerPreviousPeriodCostColumn,
    billingExplorerTrendColumn,
  ];

  if (groupBy && !groupingsExcludingForecastedCostsColumns.has(groupBy)) {
    columns.push(getBillingExplorerSingleForecastedCostColumnDefs("month"));
    columns.push(getBillingExplorerSingleForecastedCostColumnDefs("quarter"));
  }

  return columns;
};
